import React, { useState } from "react";
import { AlertTriangle, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * Account Deletion — App Store 5.1.1(v) compliance. Requires the current password AND a
 * literal "DELETE" confirmation before the backend cascade-erases every record tied to the
 * account. Irreversible; logs the user out and returns them to the public Landing page.
 */
const DeleteAccountModal = ({ open, onClose }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const canSubmit = password.length > 0 && confirmText.trim().toUpperCase() === "DELETE";

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      await api.delete("/account", { data: { password, confirm: confirmText } });
      setDone(true);
      setTimeout(() => {
        logout();
        navigate("/");
      }, 1800);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not delete your account. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[hsl(38_30%_12%_/_0.5)] backdrop-blur-sm" data-testid="delete-account-modal">
      <div className="surface rounded-[1.75rem] w-full max-w-md p-7 animate-fade-up">
        {done ? (
          <div className="text-center py-6" data-testid="delete-account-success">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="font-serif text-xl mb-1">Account deleted</p>
            <p className="text-sm text-muted-foreground">Your account and all associated data have been permanently erased. Signing you out…</p>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full flex items-center justify-center bg-red-500/10 text-red-500">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <h2 className="font-serif text-2xl text-red-500">Delete my account</h2>
              </div>
              <button data-testid="delete-account-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-muted/60 text-muted-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              This permanently erases your profile, Loved Ones, courtesy contacts, and every card you&apos;ve created.
              <span className="font-medium text-foreground"> This cannot be undone.</span>
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-muted-foreground">Confirm your password</label>
                <input data-testid="delete-account-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-500/40" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Type <span className="font-mono font-semibold">DELETE</span> to confirm</label>
                <input data-testid="delete-account-confirm-text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full mt-1 bg-input border border-border rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-500/40" />
              </div>
              {error && <div data-testid="delete-account-error" className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</div>}
              <button data-testid="delete-account-submit" onClick={submit} disabled={!canSubmit || busy}
                className="w-full bg-red-600 text-white font-medium rounded-lg py-3 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</> : "Permanently delete my account"}
              </button>
              <button onClick={onClose} className="w-full text-sm text-muted-foreground py-1.5">Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeleteAccountModal;
